"use client";

/**
 * P18-19 · The swatch preview, and the two islands that read it.
 *
 * The preview (P2-DB04 r2 §03) is the one genuinely stateful thing on a card:
 * hovering, focusing or tapping a swatch swaps the media and the price, and
 * leaving the row puts both back. That used to be a `useState` in
 * `<ProductCard>` itself, which is why the whole card — every class name, every
 * image URL, every badge — had to be a client component.
 *
 * The state's two readers (`<CardMedia>`, `<CardPrice>`) and its one writer
 * (`<SwatchRow>`) sit in different corners of the card's markup, so it travels
 * by context rather than by props. `<CardPreviewProvider>` renders no DOM: the
 * card's markup is unchanged by its presence. `useCardPreview()` degrades to a
 * fixed "nothing is previewed" outside a provider, so each island can be
 * rendered on its own — a jsdom test, the kitchen sink — without a wrapper that
 * exists only for the swatch feature.
 *
 * The readers live in this file rather than one each, deliberately: every
 * separate `"use client"` module is another client-reference entry and another
 * webpack module wrapper in the route's bundle, and splitting the card into
 * seven of them measurably grew first-load JS. One module per *concern* is the
 * balance — this one is "the preview".
 */
import { createContext, useContext, useMemo, useState } from "react";
import type { CSSProperties } from "react";

import { StoreImage, cardHoverImageUrl } from "~/components/ui/store-image";
import type { StoreImageSizePreset } from "~/components/ui/store-image";
import type { CardImageFit } from "~/lib/storefront/card-image-fit";
import { cn } from "~/lib/utils";

import { PriceDisplay } from "./price-display";
import type { ProductCardViewSwatch } from "./types";

export interface CardPreviewValue {
  /** The swatch being previewed, or null for the base product. */
  preview: ProductCardViewSwatch | null;
  setPreview: (swatch: ProductCardViewSwatch | null) => void;
}

const INERT: CardPreviewValue = { preview: null, setPreview: () => undefined };

const CardPreviewContext = createContext<CardPreviewValue | null>(null);

export function CardPreviewProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [preview, setPreview] = useState<ProductCardViewSwatch | null>(null);
  const value = useMemo(() => ({ preview, setPreview }), [preview]);
  return (
    <CardPreviewContext.Provider value={value}>
      {children}
    </CardPreviewContext.Provider>
  );
}

export function useCardPreview(): CardPreviewValue {
  return useContext(CardPreviewContext) ?? INERT;
}

/**
 * `<CardMedia>` — the card's image stack, and the byte-critical boundary on the
 * whole card.
 *
 * A `next/image` renders to roughly 1.5 KB of HTML (nine `srcset` candidates,
 * `sizes`, the inline fill `style`) and a card renders two of them; measured on
 * the demo homepage, `<img>` tags alone are 71 KB of a 271 KB document.
 * Anything a *server* component renders is serialised a second time into the
 * inlined RSC payload, so server-rendering the images would put those 71 KB in
 * the document twice. Rendered here, the payload carries only the handful of
 * scalars below and the expanded markup exists exactly once, in the HTML.
 *
 * P18-25 · Both halves of that 71 KB are now gone: the primary image renders
 * three `srcset` candidates capped at 512 px instead of nine capped at 2048
 * (`STORE_IMAGE_WIDTHS.productCard`), and the hover swap is a CSS background
 * layer rather than a second `next/image`. A card's media is ~460 B of HTML.
 *
 * Scalars, not the two `ProductCardViewImage` objects, on purpose: the card's
 * images are decorative (`alt=""` — the card link carries the accessible name),
 * so the rows' `alt` is never read here and there is no reason to serialise it,
 * or the object framing around it, once per card.
 */
export interface CardMediaProps {
  imageUrl: string | null;
  imageBlurDataUrl?: string | null;
  secondaryImageUrl?: string | null;
  preset: StoreImageSizePreset;
  /** Shared by every layer so the stack fills the ratio box identically. */
  imageClass: string;
  /** Above-the-fold grids pass true for the LCP candidate. */
  priority?: boolean;
  /** Out-of-stock dims the media (P2-DB04 §02). */
  soldOut?: boolean;
  /**
   * Interim framing for a weak legacy photo (`~/lib/storefront/card-image-fit`):
   * scaled about the subject and, for a grey sweep, on a Stone tile. The
   * photo is shown whole and edge-to-edge then, so `imageClass` is not used.
   */
  fit?: CardImageFit | null;
}

export function CardMedia({
  imageUrl,
  imageBlurDataUrl,
  secondaryImageUrl,
  preset,
  imageClass,
  priority,
  soldOut,
  fit,
}: CardMediaProps) {
  const { preview } = useCardPreview();
  const previewImageUrl = preview?.imageUrl ?? null;

  const primary = imageUrl ? (
    <StoreImage
      src={imageUrl}
      alt=""
      preset={preset}
      fill
      blurDataUrl={imageBlurDataUrl ?? undefined}
      priority={priority}
      className={cn(
        fit
          ? cn("object-contain", !fit.tint && "mix-blend-multiply")
          : imageClass,
        // Hover/focus swaps to the secondary image when one exists
        // (suppressed while a swatch preview image is showing).
        secondaryImageUrl &&
          !previewImageUrl &&
          "transition-opacity group-hover:opacity-0 group-focus-within:opacity-0",
      )}
    />
  ) : null;

  return (
    <>
      {primary && fit ? (
        <span
          className={cn(
            "absolute inset-0 overflow-hidden rounded-2xl",
            fit.tint && "bg-surface-2",
          )}
        >
          <span
            className="absolute inset-0"
            style={{
              transform: `translate(${fit.x}%, ${fit.y}%) scale(${fit.scale})`,
            }}
          >
            {primary}
          </span>
        </span>
      ) : primary ? (
        primary
      ) : (
        <span className="absolute inset-0 bg-surface-2" />
      )}
      {secondaryImageUrl && !previewImageUrl && (
        /*
         * P18-25 · The hover swap is a CSS background layer, not a second
         * `<img>`. A `next/image` is ~1.5 KB of markup in every document and a
         * fetch the browser may start before anyone hovers; this is one custom
         * property (~95 B) whose `background-image` is only declared inside
         * `.group:hover`/`:focus-within` (see globals.css § card-hover-media),
         * so the rendition is requested on the first hover and not before.
         *
         * `aria-hidden` for the same reason the `<img>` carried `alt=""`: the
         * card link is the accessible name, the media is decorative.
         */
        <span
          aria-hidden="true"
          className="card-hover-media"
          style={
            {
              "--card-hover-media": `url("${cardHoverImageUrl(secondaryImageUrl, preset)}")`,
            } as CSSProperties
          }
        />
      )}
      {previewImageUrl && (
        <StoreImage
          src={previewImageUrl}
          alt=""
          preset={preset}
          fill
          className={imageClass}
        />
      )}
      {soldOut && <span className="absolute inset-0 bg-bg/55" />}
    </>
  );
}

/**
 * `<CardPrice>` — the price line, the second thing a preview changes: previewing
 * Clay shows Clay's was/now, and the "From …" range prefix is dropped because a
 * previewed variant has one price, not a range. Rendering it here keeps
 * `<PriceDisplay>`'s markup out of the RSC payload too, at the cost of four
 * small values.
 */
export interface CardPriceProps {
  /** The base product's minimum price. */
  priceMinor: number | null;
  compareAtMinor: number | null;
  currency: string;
  /** True when the base product spans a price range ("From …"). */
  from?: boolean;
  className?: string;
}

export function CardPrice({
  priceMinor,
  compareAtMinor,
  currency,
  from,
  className,
}: CardPriceProps) {
  const { preview } = useCardPreview();
  return (
    <PriceDisplay
      priceMinor={preview ? preview.priceMinor : priceMinor}
      compareAtMinor={preview ? preview.compareAtMinor : compareAtMinor}
      currency={currency}
      from={!preview && from}
      className={className}
    />
  );
}
